import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Form, Modal, ProgressBar } from 'react-bootstrap';
import { faCloudUploadAlt, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { css, StyleSheet } from 'aphrodite';
import { useDropzone } from 'react-dropzone';

import { anchovy, colorStyles } from 'lib/css/colors';
import { client } from 'lib/networking/http';
import { REBATE_ANALYSIS_CSV_UPLOAD, UPLOAD_FILE } from 'lib/networking/endpoints';

const STATUS = {
  IDLE: 'idle',
  UPLOADING: 'uploading',
  SAVING: 'saving',
  ERROR: 'error',
};

function RebateAnalysisUploadModal({ onHide, onSuccess }) {
  const [file, setFile] = useState(null);
  const [s3Path, setS3Path] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState(STATUS.IDLE);
  const [errorMsg, setErrorMsg] = useState('');

  const onDrop = useCallback(acceptedFiles => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      setStatus(STATUS.IDLE);
      setErrorMsg('');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
  });

  const isUploading = status === STATUS.UPLOADING || status === STATUS.SAVING;

  // eslint-disable-next-line prettier/prettier
  const handleUpload = async() => {
    if (!file) return;

    setStatus(STATUS.UPLOADING);
    setProgress(0);
    setErrorMsg('');

    try {
      let resolvedS3Path = s3Path;
      if (!resolvedS3Path) {
        const formData = new FormData();
        formData.append(
          'file',
          new File([file], encodeURIComponent(file.name), { type: file.type }),
        );
        formData.append('file_path', 'assets/dpa');

        const uploadResponse = await client.post(UPLOAD_FILE, formData, {
          onUploadProgress: progressEvent => {
            const pct = progressEvent.total
              ? Math.round((progressEvent.loaded * 85) / progressEvent.total)
              : 0;
            setProgress(pct);
          },
        });

        resolvedS3Path = uploadResponse.data?.s3_path;
        if (!resolvedS3Path) throw new Error('Upload failed — no S3 path returned.');
        setS3Path(resolvedS3Path);
      }

      setStatus(STATUS.SAVING);
      setProgress(90);

      await client.post(REBATE_ANALYSIS_CSV_UPLOAD, {
        source_location_url: resolvedS3Path,
        ...(mfaCode ? { mfa_passcode: mfaCode } : {}),
      });

      setProgress(100);
      onSuccess();
      onHide();
    } catch (err) {
      const data = err.response?.data || {};
      if (data.requires_mfa) {
        setRequiresMfa(true);
        setStatus(STATUS.ERROR);
        setErrorMsg(data.message || 'Enter your Snowflake MFA code and try again.');
      } else {
        setStatus(STATUS.ERROR);
        setErrorMsg(data.message || err.message || 'Upload failed. Please try again.');
      }
    }
  };

  const statusLabel = status === STATUS.SAVING ? 'Saving record…' : `Uploading… ${progress}%`;

  return (
    <Modal
      show
      centered
      animation={false}
      onHide={
        isUploading
          ? undefined
          : () => {
              setS3Path(null);
              onHide();
            }
      }
    >
      <Modal.Header closeButton={!isUploading}>
        <Modal.Title>Upload Rebate File</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isUploading ? (
          <div className="py-3">
            <p className="text-muted mb-2">{statusLabel}</p>
            <ProgressBar
              now={progress}
              animated={status === STATUS.SAVING}
              striped
              variant="primary"
              style={{ height: '1.25rem' }}
            />
            <p className="text-muted mt-2 small">{file?.name} &mdash; do not close this window.</p>
          </div>
        ) : (
          <>
            <div
              {...getRootProps()}
              className={css(styles.dropzone, isDragActive && styles.dropzoneActive)}
            >
              <input {...getInputProps()} />
              {file ? (
                <div className="d-flex align-items-center justify-content-center">
                  <FontAwesomeIcon icon={faFileExcel} size="2x" className="mr-3 text-success" />
                  <div className="text-left">
                    <div className="font-weight-bold">{file.name}</div>
                    <div className={css(colorStyles.anchovy)}>
                      {(file.size / 1024).toFixed(1)} KB &mdash; click or drop to replace
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <FontAwesomeIcon color={anchovy} icon={faCloudUploadAlt} size="2x" />
                  <p className={css(colorStyles.anchovy, styles.dropText)}>
                    {isDragActive
                      ? 'Drop the file here…'
                      : 'Drag & drop an Excel file here, or click to select'}
                  </p>
                  <p className="text-muted small mb-0">.xlsx files only</p>
                </div>
              )}
            </div>
            {requiresMfa && (
              <Form.Group className="mt-3 mb-0">
                <Form.Label className="small font-weight-bold mb-1">Snowflake MFA Code</Form.Label>
                <Form.Control
                  autoFocus
                  type="password"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="6-digit code from your authenticator app"
                  value={mfaCode}
                  onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
                  size="sm"
                />
              </Form.Group>
            )}
            {status === STATUS.ERROR && <p className="text-danger mt-2 small">{errorMsg}</p>}
          </>
        )}
      </Modal.Body>
      {!isUploading && (
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpload} disabled={!file}>
            Upload
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  dropzone: {
    border: `2px dashed ${anchovy}`,
    borderRadius: '6px',
    cursor: 'pointer',
    minHeight: '140px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '24px',
    background: 'white',
    ':hover': {
      borderColor: '#007bff',
    },
  },
  dropzoneActive: {
    borderColor: '#007bff',
    background: '#f0f7ff',
  },
  dropText: {
    marginTop: '8px',
    marginBottom: '4px',
  },
});

RebateAnalysisUploadModal.propTypes = {
  onHide: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default RebateAnalysisUploadModal;
